/**
 * What the viewer may do in a fund. The backend is the authority: since the
 * «بانی» role it returns `capabilities` on the fund detail, derived from the
 * viewer's role and the fund's banker switches, and it enforces the same
 * matrix on every endpoint. Gate UI on these flags — never on
 * `role === 'mgr' || role === 'own'` — so a banker sees exactly the buttons the
 * server will accept.
 *
 *   manage          everything a manager does (settings, members, loans,
 *                   withdrawal cartable, bank payouts…)
 *   view_finance    balances, the management/financial dashboards, Excel,
 *                   every member's loans + transactions
 *   record_cashin   record a cash-in / installment that didn't go through a
 *                   gateway («ثبت پرداخت»)
 *   record_cashout  record a cash-out that didn't go through Hamyan
 *                   («برداشت تنها ثبت شود» — never a bank payout)
 */

export interface IResolvedCapabilities extends IFundCapabilities {
    role: string | undefined
    isBanker: boolean
    /** the viewer's own poolship in this fund */
    poolshipId: number | undefined
}

const NONE: IFundCapabilities = {
    manage: false,
    view_finance: false,
    record_cashin: false,
    record_cashout: false,
}

/**
 * Role-only answer, for a backend that predates `capabilities` (or before the
 * fund detail has loaded, from a role passed in navigation state). A banker
 * gets the defaults of the fund's banker switches.
 */
export function capabilitiesForRole(role?: string | null): IFundCapabilities {
    switch (role) {
        case 'own':
        case 'mgr':
            return {
                manage: true,
                view_finance: true,
                record_cashin: true,
                record_cashout: true,
            }
        case 'bnk':
            return {
                manage: false,
                view_finance: true,
                record_cashin: true,
                record_cashout: false,
            }
        default:
            return NONE
    }
}

interface IFundLike {
    capabilities?: Partial<IFundCapabilities> | null
    poolship?: { role?: string | null; id?: number | null } | null
}

export function capabilitiesFor(
    fund?: IFundLike | null,
    fallbackRole?: string | null
): IResolvedCapabilities {
    const role = fund?.poolship?.role ?? fallbackRole ?? undefined
    const caps = fund?.capabilities
    const base: IFundCapabilities = caps
        ? {
              manage: Boolean(caps.manage),
              view_finance: Boolean(caps.view_finance),
              record_cashin: Boolean(caps.record_cashin),
              record_cashout: Boolean(caps.record_cashout),
          }
        : capabilitiesForRole(role)
    return {
        ...base,
        role,
        isBanker: role === 'bnk',
        poolshipId: fund?.poolship?.id ?? undefined,
    }
}
